import { useCallback, useEffect, useMemo, useState } from "react";
import { useAdmin } from "../admin";
import PhonePreview from "../components/PhonePreview";
import StatusChip from "../components/StatusChip";
import { shortDate, weekStart } from "../lib/dates";
import { LANG_NAMES, LANGS, languageComplete, normalise, ROLES, roleName, STATUS_NAMES, titleOf } from "../lib/scenario";
import type { Lang, Role, ScenarioRow, Status } from "../lib/scenario";
import { listScenarios } from "../lib/scenarios";
import { errorText, supabase } from "../supabase";

// Screen 13: the training lead finds, approves and assigns scenarios.

export default function Library() {
  const { sites } = useAdmin();
  const [rows, setRows] = useState<ScenarioRow[] | null>(null);
  const [error, setError] = useState("");
  const [role, setRole] = useState("");
  const [site, setSite] = useState("");
  const [lang, setLang] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(() => {
    listScenarios()
      .then(setRows)
      .catch((e) => setError(errorText(e)));
  }, []);
  useEffect(load, [load]);

  const shown = useMemo(() => {
    const words = search.trim().toLowerCase();
    return (rows ?? []).filter((r) => {
      if (role && !r.roles.includes(role)) return false;
      if (status && r.status !== status) return false;
      if (lang && !languageComplete(normalise(r.content), lang as Lang)) return false;
      if (site && r.scenario_site.length > 0 && !r.scenario_site.some((s) => s.site_id === site)) return false;
      if (words) {
        const text = [r.slug, ...LANGS.map((l) => r.content.text?.[l]?.title ?? "")].join(" ").toLowerCase();
        if (!text.includes(words)) return false;
      }
      return true;
    });
  }, [rows, role, site, lang, status, search]);

  const current = rows?.find((r) => r.id === selected) ?? null;
  const filtered = !!(role || site || lang || status || search.trim());

  return (
    <>
      <div className="page-head">
        <h1>Scenarios</h1>
        <a className="btn" href="#/scenarios/new">
          New scenario
        </a>
      </div>
      <div className="filters">
        <input type="search" placeholder="Search titles" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select aria-label="Role" value={role} onChange={(e) => setRole(e.target.value)}>
          <option value="">All roles</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {roleName(r)}
            </option>
          ))}
        </select>
        <select aria-label="Site" value={site} onChange={(e) => setSite(e.target.value)}>
          <option value="">All sites</option>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <select aria-label="Language" value={lang} onChange={(e) => setLang(e.target.value)}>
          <option value="">All languages</option>
          {LANGS.map((l) => (
            <option key={l} value={l}>
              {LANG_NAMES[l]} complete
            </option>
          ))}
        </select>
        <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          {(Object.keys(STATUS_NAMES) as Status[]).map((s) => (
            <option key={s} value={s}>
              {STATUS_NAMES[s]}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="notice error">{error}</div>}
      <div className={current ? "with-panel" : ""}>
        <div className="card table-card">
          {!rows && !error && <p className="empty">Loading…</p>}
          {rows && rows.length === 0 && (
            <div className="empty stack" style={{ alignItems: "center" }}>
              <p>No scenarios yet. Write the first one, or ask for the sample scenarios to be published.</p>
              <a className="btn" href="#/scenarios/new">
                New scenario
              </a>
            </div>
          )}
          {rows && rows.length > 0 && shown.length === 0 && (
            <p className="empty">{filtered ? "No scenarios match these filters." : "No scenarios."}</p>
          )}
          {shown.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Roles</th>
                  <th>Languages</th>
                  <th>Status</th>
                  <th>Last updated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const content = normalise(r.content);
                  return (
                    <tr
                      key={r.id}
                      className={`clickable ${r.id === selected ? "selected" : ""}`}
                      onClick={() => setSelected(r.id === selected ? null : r.id)}
                    >
                      <td>
                        <strong>{titleOf(content)}</strong>
                        <div className="small muted">
                          {r.slug} · v{r.version}
                        </div>
                      </td>
                      <td className="small">{r.roles.map(roleName).join(", ") || <span className="muted">None yet</span>}</td>
                      <td>
                        <span className="chips">
                          {LANGS.map((l) => {
                            const done = languageComplete(content, l);
                            return (
                              <span key={l} className="chip" title={done ? "Complete" : "Missing text"}>
                                <span className={`dot ${done ? "ok" : ""}`} /> {l.toUpperCase()}
                              </span>
                            );
                          })}
                        </span>
                      </td>
                      <td>
                        <StatusChip row={r} />
                      </td>
                      <td className="small">{shortDate(r.scenario_change?.updated_at ?? r.updated_at)}</td>
                      <td>
                        <a href={`#/scenarios/${r.id}`} onClick={(e) => e.stopPropagation()}>
                          Edit
                        </a>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
        {current && <Panel key={current.id} row={current} onChanged={load} onClose={() => setSelected(null)} />}
      </div>
    </>
  );
}

function Panel({ row, onChanged, onClose }: { row: ScenarioRow; onChanged: () => void; onClose: () => void }) {
  const { sites } = useAdmin();
  const [showChange, setShowChange] = useState(false);
  const [message, setMessage] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const content = normalise(showChange && row.scenario_change ? row.scenario_change.content : row.content);

  async function act(run: () => PromiseLike<{ error: unknown }>, done: string) {
    setBusy(true);
    setMessage(null);
    const { error } = await run();
    setBusy(false);
    if (error) setMessage({ kind: "error", text: errorText(error) });
    else {
      setMessage({ kind: "ok", text: done });
      onChanged();
    }
  }

  return (
    <aside className="card panel stack">
      <div className="row" style={{ justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <h2 style={{ marginBottom: 4 }}>{titleOf(normalise(row.content))}</h2>
          <StatusChip row={row} />
        </div>
        <button className="btn quiet small" onClick={onClose} aria-label="Close">
          ✕
        </button>
      </div>
      {row.scenario_change && (
        <div className="segmented">
          <button className={!showChange ? "on" : ""} onClick={() => setShowChange(false)}>
            On phones (v{row.version})
          </button>
          <button className={showChange ? "on" : ""} onClick={() => setShowChange(true)}>
            Changes
          </button>
        </div>
      )}
      <PhonePreview content={content} />
      {message && <div className={`notice ${message.kind}`}>{message.text}</div>}
      <SitesSetting row={row} onSaved={onChanged} />
      {row.status === "approved" && <SendToSite row={row} />}
      <div className="row">
        <a className="btn outline small" href={`#/scenarios/${row.id}`}>
          Edit
        </a>
        {row.status === "approved" && (
          <ConfirmButton
            label="Retire"
            confirm="Retire it? It stops being given out at once. Past practice stays."
            disabled={busy}
            onConfirm={() =>
              act(
                () => supabase.rpc("admin_set_scenario_status", { p_id: row.id, p_status: "retired" }),
                "Retired. It is no longer given out; its practice history stays.",
              )
            }
          />
        )}
        {row.status === "retired" && (
          <button
            className="btn quiet small"
            disabled={busy}
            onClick={() =>
              act(
                () => supabase.rpc("admin_set_scenario_status", { p_id: row.id, p_status: "draft" }),
                "Restored to draft. Send it for review again to bring it back.",
              )
            }
          >
            Restore to draft
          </button>
        )}
      </div>
      {sites.length === 0 && <p className="small muted">Add sites in Settings to choose where it goes.</p>}
    </aside>
  );
}

export function ConfirmButton(props: {
  label: string;
  confirm: string;
  disabled?: boolean;
  quiet?: boolean;
  onConfirm: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const tone = props.quiet ? "quiet" : "danger";
  if (!asking) {
    return (
      <button className={`btn ${tone} small`} disabled={props.disabled} onClick={() => setAsking(true)}>
        {props.label}
      </button>
    );
  }
  return (
    <span className="row small">
      <span>{props.confirm}</span>
      <button
        className={`btn ${tone} small`}
        onClick={() => {
          setAsking(false);
          props.onConfirm();
        }}
      >
        Yes, {props.label.toLowerCase()}
      </button>
      <button className="btn quiet small" onClick={() => setAsking(false)}>
        Cancel
      </button>
    </span>
  );
}

/** Which sites get the scenario in the weekly cycle. No sites chosen means every site. */
function SitesSetting({ row, onSaved }: { row: ScenarioRow; onSaved: () => void }) {
  const { sites } = useAdmin();
  const saved = row.scenario_site.map((s) => s.site_id);
  const [every, setEvery] = useState(saved.length === 0);
  const [chosen, setChosen] = useState<string[]>(saved);
  const [state, setState] = useState("");
  if (sites.length === 0) return null;
  const changed = every !== (saved.length === 0) || (!every && [...chosen].sort().join() !== [...saved].sort().join());

  async function save() {
    setState("Saving…");
    const { error } = await supabase.rpc("admin_set_scenario_sites", { p_id: row.id, p_sites: every ? [] : chosen });
    setState(error ? errorText(error) : "Saved.");
    if (!error) onSaved();
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <h3>Weekly practice goes to</h3>
      <label className="check">
        <input type="radio" checked={every} onChange={() => setEvery(true)} /> Every site
      </label>
      <label className="check">
        <input type="radio" checked={!every} onChange={() => setEvery(false)} /> Chosen sites
      </label>
      {!every && (
        <div className="stack" style={{ gap: 4, paddingLeft: 26 }}>
          {sites.map((s) => (
            <label key={s.id} className="check">
              <input
                type="checkbox"
                checked={chosen.includes(s.id)}
                onChange={(e) => setChosen(e.target.checked ? [...chosen, s.id] : chosen.filter((c) => c !== s.id))}
              />
              {s.name}
            </label>
          ))}
        </div>
      )}
      <div className="row">
        <button className="btn small" disabled={!changed || (!every && chosen.length === 0)} onClick={save}>
          Save sites
        </button>
        <span className="small muted">{state}</span>
      </div>
    </div>
  );
}

/** "Assign to site": send an approved scenario to a site now, outside the weekly cycle. */
function SendToSite({ row }: { row: ScenarioRow }) {
  const { sites } = useAdmin();
  const [site, setSite] = useState(sites[0]?.id ?? "");
  const [role, setRole] = useState("");
  const [sent, setSent] = useState<{ site_id: string; role: string | null }[]>([]);
  const [state, setState] = useState("");
  const week = weekStart();

  const loadSent = useCallback(() => {
    supabase
      .from("assignment")
      .select("site_id, role")
      .eq("scenario_id", row.id)
      .eq("kind", "push")
      .eq("week_start", week)
      .then(({ data }) => setSent((data as typeof sent) ?? []));
  }, [row.id, week]);
  useEffect(loadSent, [loadSent]);

  if (sites.length === 0) return null;
  const siteName = (id: string) => sites.find((s) => s.id === id)?.name ?? "a site";

  async function send() {
    setState("Sending…");
    const { error } = await supabase.rpc("push_scenario", { p_scenario: row.id, p_site: site, p_role: role || null });
    setState(error ? errorText(error) : `Sent to ${siteName(site)}. Phones get it at their next sync.`);
    loadSent();
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <h3>Assign to a site this week</h3>
      <p className="small muted">Everyone there gets it this week, on top of their weekly practice.</p>
      <div className="row">
        <select aria-label="Site to send to" value={site} onChange={(e) => setSite(e.target.value)} style={{ flex: 1 }}>
          {sites.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <select aria-label="Role to send to" value={role} onChange={(e) => setRole(e.target.value)} style={{ flex: 1 }}>
          <option value="">Everyone</option>
          {row.roles.map((r) => (
            <option key={r} value={r}>
              {roleName(r as Role)}
            </option>
          ))}
        </select>
      </div>
      <div className="row">
        <button className="btn small" onClick={send} disabled={!site}>
          Send now
        </button>
        <span className="small muted">{state}</span>
      </div>
      {sent.length > 0 && (
        <p className="small muted">
          Sent this week to{" "}
          {sent.map((s) => siteName(s.site_id) + (s.role ? ` (${roleName(s.role)})` : "")).join(", ")}.
        </p>
      )}
    </div>
  );
}
